import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Row, Col } from "react-grid-system";
import { Plus } from "lucide-react";
import showAlert from "../../../server/ShowAlert";
import { createPromotion, updatePromotion } from "../../../server/PriceList";
import FamilyChangeDialog from "./FamilyChangeDialog";
import { getFamilyCategory } from "./familyCategoryUtils";
import { roundMoney } from "../../../utils/formatPrice";
import {
  formatDateTimeForJerusalem,
  getGtinsFromPromotionEntries,
  getManufacturerPackQuantity,
  getProductCurrentPrice,
  getPromoId,
  getPromoStatusLabel,
  getPromoStatusSelectOptions,
  getPromotionApiErrorMessage,
  hasValidPackQuantity,
  isQuantityBasedPromoType,
  PROMO_TYPES,
  toDateInputValue,
  validatePromotionBeforeSave,
} from "../sales/promotionUtils";

const toLinkedUnitPrice = (packagePrice, packQty) => {
  const total = parseFloat(packagePrice);
  const qty = Number(packQty);
  if (!Number.isFinite(total) || qty <= 0) return "";
  return String(roundMoney(total / qty));
};

const toLinkedPackagePrice = (unitPrice, packQty) => {
  const unit = parseFloat(unitPrice);
  const qty = Number(packQty);
  if (!Number.isFinite(unit) || qty <= 0) return "";
  return String(roundMoney(unit * qty));
};

const resolvePromoTargetProducts = (promo, product) => {
  const fromPromo = getGtinsFromPromotionEntries(promo ? [promo] : []);
  if (fromPromo.length > 0) return fromPromo;
  return product?.gtin ? [product.gtin] : [];
};

const SKIPPED_PRODUCTS_SHOWN = 5;

const describeSkippedProducts = (products) => {
  const labels = products.map(
    (prod) => `${prod?.name || "מוצר"} (${prod?.gtin || "ללא ברקוד"})`,
  );
  const shown = labels.slice(0, SKIPPED_PRODUCTS_SHOWN).join(", ");
  const rest =
    labels.length > SKIPPED_PRODUCTS_SHOWN
      ? ` ועוד ${labels.length - SKIPPED_PRODUCTS_SHOWN}`
      : "";
  return `הפריטים הבאים לא נכללו במבצע כי לא מוגדרת להם כמות במארז יצרן: ${shown}${rest}`;
};

/** One promotion per distinct pack quantity - the backend holds a single minQuantity. */
const groupGtinsByPackQuantity = (products) => {
  const groups = new Map();

  for (const prod of products) {
    const packQuantity = getManufacturerPackQuantity(prod);
    const gtins = groups.get(packQuantity) || [];
    gtins.push(String(prod.gtin));
    groups.set(packQuantity, gtins);
  }

  return groups;
};

const Promotion = ({
  promo = 0,
  product,
  close = null,
  onCreated = null,
  inline = false,
  onAddPromo = null,
  existingPromotions = [],
}) => {
  const [promoType, setPromoType] = useState("FLAT");
  const [discauntValue, setDiscauntValue] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [promoStatus, setPromoStatus] = useState("ACTIVE");
  const [promoStartDate, setPromoStartDate] = useState(() =>
    toDateInputValue(new Date()),
  );
  const [promoEndDate, setPromoEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [isSavingPromo, setIsSavingPromo] = useState(false);
  const [minQuantity, setMinQuantity] = useState("");
  const [maxPerPurchase, setMaxPerPurchase] = useState(null);
  const [maxPerPeriod, setMaxPerPeriod] = useState(null);
  const [totalAllocatedQty, setTotalAllocatedQty] = useState(null);
  const [changes, setChanges] = useState({
    targetProducts: resolvePromoTargetProducts(
      promo && promo !== 0 ? promo : null,
      product,
    ),
  });
  const [showFamilyDialog, setShowFamilyDialog] = useState(false);

  const stationId = useSelector((state) => state.user.stationId);
  const itemsShow = useSelector((state) => state.displayProduct.itemsShow);
  const categories = useSelector(
    (state) => state.selectedProduct.productCategories,
  );
  const productPricing = useSelector(
    (state) => state.selectedProduct.productPricing,
  );
  const familyCategory = getFamilyCategory(categories);

  const storeProducts = useMemo(() => {
    if (Array.isArray(itemsShow)) return itemsShow;
    if (Array.isArray(itemsShow?.products)) return itemsShow.products;
    return [];
  }, [itemsShow]);

  const productGtin = product?.gtin;

  const familyMarkedProducts = useMemo(() => {
    const targetProducts = changes.targetProducts || [];
    const fromStore = storeProducts.filter((p) =>
      targetProducts.includes(p.gtin),
    );
    if (fromStore.length > 0) return fromStore;
    return product ? [product] : [];
  }, [storeProducts, changes.targetProducts, product]);

  const isPackagePromoType = promoType === PROMO_TYPES.PACKAGE_PRICE;
  // Both quantity based types replace the free-text quantity fields with their own.
  const hidesQuantityFields = isQuantityBasedPromoType(promoType);
  const packQuantity = getManufacturerPackQuantity(product);
  // Without a manufacturer pack there is nothing to price a package by.
  const canCreatePackagePromo = hasValidPackQuantity(product);

  useEffect(() => {
    if (
      promo === 0 &&
      promoType === PROMO_TYPES.PACKAGE_PRICE &&
      !canCreatePackagePromo
    ) {
      setPromoType(PROMO_TYPES.FLAT);
    }
  }, [promo, promoType, canCreatePackagePromo]);

  useEffect(() => {
    if (promo !== 0) {
      setPromoType(promo.promoType);
      setPromoStatus(promo.status);
      setDiscauntValue(promo.discountValue);
      setMinQuantity(promo.minQuantity);
      setPricePerUnit(
        promo.promoType === PROMO_TYPES.PACKAGE_PRICE
          ? toLinkedUnitPrice(promo.discountValue, promo.minQuantity)
          : "",
      );
      setPromoStartDate(promo.startDate.split("T")[0]);
      setPromoEndDate(promo.endDate.split("T")[0]);
      setDescription(promo.description);
      setMinQuantity(promo.minQuantity);
      setMaxPerPeriod(promo.maxPerPeriod);
      setMaxPerPurchase(
        promo.maxPerPurchase === 0 ? null : promo.maxPerPurchase,
      );
      setTotalAllocatedQty(promo.totalAllocatedQty);
      setChanges({
        targetProducts: resolvePromoTargetProducts(
          promo,
          productGtin ? { gtin: productGtin } : null,
        ),
      });
    } else {
      setPromoStartDate(toDateInputValue(new Date()));
      setPricePerUnit("");
    }
  }, [promo, productGtin]);

  const handleChange = (field, value) => {
    setChanges((prev) => ({ ...prev, [field]: value }));
  };

  const buildProductPriceByGtin = (gtins, productsForTargets = []) => {
    const priceByGtin = {};
    for (const gtin of gtins) {
      const gtinStr = String(gtin);
      const fromTargets = productsForTargets.find(
        (p) => String(p?.gtin) === gtinStr,
      );
      const fromStore = storeProducts.find((p) => String(p?.gtin) === gtinStr);
      const fromMain =
        product && String(product.gtin) === gtinStr ? product : null;
      const resolved = fromTargets || fromStore || fromMain;
      priceByGtin[gtinStr] = getProductCurrentPrice(resolved, productPricing);
    }
    return priceByGtin;
  };

  const showPromoValidationError = (title, message) => {
    showAlert({
      msgTitle: title,
      msgText: message,
      msgType: "error",
    });
  };

  const getPromotionValidationError = ({
    targetGtins,
    productsForTargets = [],
    discountValue,
    status,
    startDate,
    endDate,
    currentPromoId = null,
    minQuantityOverride = undefined,
  }) => {
    const resolvedMinQuantity =
      minQuantityOverride !== undefined
        ? minQuantityOverride
        : minQuantity === ""
          ? null
          : Number(minQuantity);
    return validatePromotionBeforeSave({
      branch: stationId,
      promoType,
      status,
      discountValue,
      minQuantity: resolvedMinQuantity,
      startDate,
      endDate,
      targetGtins,
      productPriceByGtin: buildProductPriceByGtin(
        targetGtins,
        productsForTargets,
      ),
      existingPromotions,
      currentPromoId,
    });
  };

  const handleSavePromo = async () => {
    const editableChangeKeys = Object.keys(changes).filter(
      (key) => key !== "targetProducts",
    );
    if (promo !== 0 && editableChangeKeys.length === 0) {
      showAlert({ msgTitle: "אין שינויים", msgType: "error" });
      return;
    }

    if (familyCategory) {
      setShowFamilyDialog(true);
      return;
    }

    if (promo === 0) createNewPromotion(changes, product ? [product] : []);
    else upadateExistingPromotion(changes);
  };

  /** Match every target gtin back to the product record that carries logisticsDetails. */
  const resolveTargetProducts = (gtins, selectedProducts) => {
    const pool = [
      ...selectedProducts,
      ...storeProducts,
      ...(product ? [product] : []),
    ];

    return (gtins || []).map((gtin) => {
      const match = pool.find((prod) => String(prod?.gtin) === String(gtin));
      return match || { gtin };
    });
  };

  const createNewPromotion = async (
    updatedChanges,
    productsForTargets = [],
  ) => {
    try {
      const startDate = formatDateTimeForJerusalem(promoStartDate);
      const endDate = formatDateTimeForJerusalem(promoEndDate, {
        endOfDay: true,
      });
      const discountValue =
        updatedChanges.discountValue ??
        (discauntValue === "" ? undefined : Number(discauntValue));
      const targetGtins =
        updatedChanges.targetProducts ||
        resolvePromoTargetProducts(null, product);

      if (isPackagePromoType && !canCreatePackagePromo) {
        showAlert({
          msgTitle: "לא ניתן ליצור מבצע מארז",
          msgText: "למוצר לא מוגדרת כמות במארז יצרן",
          msgType: "error",
        });
        return;
      }

      if (isPackagePromoType && !discauntValue) {
        showAlert({ msgTitle: "אנא הזן מחיר מארז", msgType: "error" });
        return;
      }

      const newPromoData = {
        branch: stationId.toString(),
        status: promoStatus,
        promoType: promoType,
        retailer: "Hamazon",
        minQuantity: minQuantity === "" ? null : minQuantity,
        maxPerPurchase: maxPerPurchase ? maxPerPeriod : 0,
        maxPerPeriod: maxPerPeriod,
        totalAllocatedQty: totalAllocatedQty,
        ...updatedChanges,
        targetProducts: targetGtins,
        startDate,
        endDate,
        discountValue,
      };

      let skippedMessage = null;

      if (isPackagePromoType) {
        const targeted = resolveTargetProducts(
          newPromoData.targetProducts,
          productsForTargets,
        );
        const eligible = targeted.filter(hasValidPackQuantity);
        const skipped = targeted.filter((prod) => !hasValidPackQuantity(prod));

        if (eligible.length === 0) {
          showAlert({
            msgTitle: "לא ניתן ליצור מבצע מארז",
            msgText: "לאף אחד מהמוצרים שנבחרו לא מוגדרת כמות במארז יצרן",
            msgType: "error",
          });
          return;
        }

        for (const [groupPackQuantity, gtins] of groupGtinsByPackQuantity(
          eligible,
        )) {
          const validationError = getPromotionValidationError({
            targetGtins: gtins,
            productsForTargets: eligible,
            discountValue,
            status: promoStatus,
            startDate,
            endDate,
            minQuantityOverride: groupPackQuantity,
          });
          if (validationError) {
            showPromoValidationError("לא ניתן ליצור את המבצע", validationError);
            return;
          }
        }

        setIsSavingPromo(true);
        for (const [groupPackQuantity, gtins] of groupGtinsByPackQuantity(
          eligible,
        )) {
          await createPromotion({
            ...newPromoData,
            minQuantity: groupPackQuantity,
            targetProducts: gtins,
          });
        }

        if (skipped.length > 0)
          skippedMessage = describeSkippedProducts(skipped);
      } else {
        const validationError = getPromotionValidationError({
          targetGtins,
          productsForTargets,
          discountValue,
          status: promoStatus,
          startDate,
          endDate,
        });
        if (validationError) {
          showPromoValidationError("לא ניתן ליצור את המבצע", validationError);
          return;
        }

        setIsSavingPromo(true);
        await createPromotion(newPromoData);
      }

      showAlert(
        skippedMessage
          ? {
              msgTitle: "המבצע נוצר בהצלחה",
              msgText: skippedMessage,
              msgType: "warning",
              msgTimer: 10000,
            }
          : { msgTitle: "המבצע נוצר בהצלחה", msgType: "success" },
      );

      setChanges({});
      onCreated?.();
      close();
    } catch (error) {
      const message = getPromotionApiErrorMessage(error, "שגיאה ביצירת מבצע");
      showAlert({
        msgTitle: "לא ניתן ליצור את המבצע",
        msgText: message,
        msgType: "error",
      });
    } finally {
      setIsSavingPromo(false);
    }
    return;
  };

  const upadateExistingPromotion = async (
    updatedChanges,
    productsForTargets = [],
  ) => {
    try {
      const startDate = formatDateTimeForJerusalem(
        updatedChanges.startDate
          ? String(updatedChanges.startDate).split("T")[0]
          : promoStartDate,
      );
      const endDate = formatDateTimeForJerusalem(
        updatedChanges.endDate
          ? String(updatedChanges.endDate).split("T")[0]
          : promoEndDate,
        { endOfDay: true },
      );
      const discountValue =
        updatedChanges.discountValue ??
        (discauntValue === "" ? undefined : Number(discauntValue));
      const status = updatedChanges.status ?? promoStatus;
      const targetGtins =
        updatedChanges.targetProducts ||
        resolvePromoTargetProducts(promo, product);

      const validationError = getPromotionValidationError({
        targetGtins,
        productsForTargets,
        discountValue,
        status,
        startDate,
        endDate,
        currentPromoId: getPromoId(promo),
      });
      if (validationError) {
        showPromoValidationError("לא ניתן לעדכן את המבצע", validationError);
        return;
      }

      setIsSavingPromo(true);
      const updatePayload = {
        ...updatedChanges,
        ...(updatedChanges.startDate || updatedChanges.endDate
          ? { startDate, endDate }
          : {}),
      };
      // Expired status is date-driven in APS — omit status so dates can recompute it.
      if (promo?.status === "EXPIRED") {
        delete updatePayload.status;
      }
      await updatePromotion(promo.promoId, updatePayload);

      showAlert({
        msgTitle: "השינויים עודכנו בהצלחה",
        msgType: "success",
      });

      setChanges({});
      onCreated?.();
      close();
    } catch (error) {
      const message = getPromotionApiErrorMessage(error, "שגיאה בעדכון");
      showAlert({
        msgTitle: "לא ניתן לעדכן את המבצע",
        msgText: message,
        msgType: "error",
      });
    } finally {
      setIsSavingPromo(false);
    }
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  const setEndOfMonth = () => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatted = formatDateLocal(endOfMonth);

    setPromoEndDate(formatted);
    handleChange(
      "endDate",
      formatDateTimeForJerusalem(formatted, { endOfDay: true }),
    );
  };

  const setEndOfYear = () => {
    const now = new Date();
    const endOfYear = new Date(now.getFullYear(), 11, 31);

    const formatted = formatDateLocal(endOfYear);

    setPromoEndDate(formatted);
    handleChange(
      "endDate",
      formatDateTimeForJerusalem(formatted, { endOfDay: true }),
    );
  };

  const handleSaveFamilyPromo = (productsToChange) => {
    const updatedChanges = { ...changes };
    updatedChanges["targetProducts"] = productsToChange.map(
      (prod) => prod.gtin,
    );
    if (promo === 0) {
      createNewPromotion(updatedChanges, productsToChange);
    } else {
      upadateExistingPromotion(updatedChanges, productsToChange);
    }
    setShowFamilyDialog(false);
  };

  const hasPendingChanges = Object.keys(changes).some(
    (key) => key !== "targetProducts",
  );
  const packagePromoBlocked =
    promo === 0 && isPackagePromoType && !canCreatePackagePromo;
  const showSaveButton =
    (promo === 0 || hasPendingChanges) && !packagePromoBlocked;

  return (
    <>
      <div
        className={[
          promo !== 0 ? "section-card" : "new-promo-section",
          inline && promo !== 0 ? "promotion-panel--inline" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <h3
          className={`section-title${onAddPromo ? " section-title--with-add" : ""}`}
        >
          <span>{promo === 0 ? "יצירת מבצע" : `מבצע ${promo.promoId}`}</span>
          {onAddPromo ? (
            <button
              type="button"
              className="promo-add-btn"
              onClick={onAddPromo}
              aria-label="יצירת מבצע חדש"
              title="יצירת מבצע חדש"
            >
              <Plus size={18} strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
        </h3>
        <Row className="promo-type-row">
          <Col sm={2}>
            <div className="field-group">
              <label className="field-label">סטטוס</label>
              {promo !== 0 && promoStatus === "EXPIRED" ? (
                <div
                  className="promo-type-select promo-type-select--readonly"
                  aria-readonly="true"
                >
                  {getPromoStatusLabel(promoStatus)}
                </div>
              ) : (
                <select
                  className="promo-type-select"
                  value={promoStatus}
                  onChange={(e) => {
                    setPromoStatus(e.target.value);
                    handleChange("status", e.target.value);
                  }}
                >
                  {getPromoStatusSelectOptions(promoStatus).map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={Boolean(option.disabled)}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </Col>
          <Col sm={2}>
            <div className="field-group">
              <label className="field-label">סוג</label>
              <select
                className="promo-type-select"
                value={promoType}
                title={
                  canCreatePackagePromo
                    ? undefined
                    : "למוצר לא מוגדרת כמות במארז יצרן"
                }
                onChange={(e) => {
                  const nextType = e.target.value;
                  if (
                    nextType === PROMO_TYPES.PACKAGE_PRICE &&
                    !canCreatePackagePromo
                  ) {
                    return;
                  }
                  setPromoType(nextType);
                  handleChange("promoType", nextType);
                  if (
                    nextType === PROMO_TYPES.PACKAGE_PRICE &&
                    packQuantity != null
                  ) {
                    setMinQuantity(packQuantity);
                    handleChange("minQuantity", packQuantity);
                    setPricePerUnit(
                      toLinkedUnitPrice(discauntValue, packQuantity),
                    );
                  } else {
                    setPricePerUnit("");
                  }
                }}
              >
                <option value="FLAT">מחיר</option>
                <option value="PERCENT">אחוז הנחה</option>
                <option value="QUANTITY_PRICE">X ב-Y</option>
                <option
                  value="PACKAGE_PRICE"
                  disabled={
                    !canCreatePackagePromo &&
                    promoType !== PROMO_TYPES.PACKAGE_PRICE
                  }
                  title={
                    canCreatePackagePromo
                      ? undefined
                      : "למוצר לא מוגדרת כמות במארז יצרן"
                  }
                >
                  מחיר מארז
                </option>
              </select>
            </div>
          </Col>
          {promoType === "FLAT" && (
            <Col sm={2.5}>
              <div className="field-group">
                <label className="field-label">מחיר</label>
                <input
                  type="number"
                  step="0.1"
                  className="number-input price-input highlighted"
                  placeholder="0.0"
                  value={discauntValue ?? ""}
                  onChange={(e) => {
                    setDiscauntValue(e.target.value);
                    handleChange("discountValue", parseFloat(e.target.value));
                  }}
                />
              </div>
            </Col>
          )}

          {promoType === "PERCENT" && (
            <Col sm={2}>
              <div className="field-group">
                <label className="field-label">אחוז הנחה (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className="number-input highlighted"
                  placeholder="0-100"
                  value={discauntValue || ""}
                  onChange={(e) => {
                    setDiscauntValue(e.target.value);
                    handleChange("discountValue", parseFloat(e.target.value));
                  }}
                />
              </div>
            </Col>
          )}

          {!hidesQuantityFields && (
            <Col sm={2}>
              <div className="field-group">
                <label className="field-label">מינימום</label>
                <input
                  type="number"
                  className="number-input"
                  placeholder="0"
                  value={minQuantity ?? ""}
                  onChange={(e) => {
                    setMinQuantity(e.target.value);
                    handleChange("minQuantity", parseInt(e.target.value));
                  }}
                />
              </div>
            </Col>
          )}

          {promoType === "QUANTITY_PRICE" && (
            <>
              <Col sm={2}>
                <div className="field-group">
                  <label className="field-label">כמות</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="number-input highlighted"
                    placeholder="יח'"
                    value={minQuantity || ""}
                    onChange={(e) => {
                      setMinQuantity(e.target.value);
                      handleChange("minQuantity", parseInt(e.target.value));
                    }}
                  />
                </div>
              </Col>
              <Col sm={2}>
                <div className="field-group">
                  <label className="field-label">מחיר</label>
                  <input
                    type="number"
                    step="0.01"
                    className="number-input price-input highlighted"
                    placeholder="0.00"
                    value={discauntValue || ""}
                    onChange={(e) => {
                      setDiscauntValue(e.target.value);
                      handleChange("discountValue", parseFloat(e.target.value));
                    }}
                  />
                </div>
              </Col>
            </>
          )}

          {isPackagePromoType && (canCreatePackagePromo || promo !== 0) && (
            <>
              <Col sm={2}>
                <div className="field-group">
                  <label className="field-label">כמות במארז יצרן</label>
                  <input
                    type="text"
                    className="number-input"
                    readOnly
                    aria-readonly="true"
                    value={packQuantity || minQuantity || ""}
                  />
                </div>
              </Col>
              <Col sm={2}>
                <div className="field-group">
                  <label className="field-label">מחיר ליחידה</label>
                  <input
                    type="number"
                    step="0.01"
                    className="number-input price-input highlighted"
                    placeholder="0.00"
                    value={pricePerUnit}
                    onChange={(e) => {
                      const nextUnitPrice = e.target.value;
                      const nextPackagePrice = toLinkedPackagePrice(
                        nextUnitPrice,
                        packQuantity || minQuantity,
                      );
                      setPricePerUnit(nextUnitPrice);
                      setDiscauntValue(nextPackagePrice);
                      handleChange(
                        "discountValue",
                        nextPackagePrice === ""
                          ? undefined
                          : parseFloat(nextPackagePrice),
                      );
                    }}
                  />
                </div>
              </Col>
              <Col sm={2}>
                <div className="field-group">
                  <label className="field-label">מחיר מארז</label>
                  <input
                    type="number"
                    step="0.01"
                    className="number-input price-input highlighted"
                    placeholder="0.00"
                    value={discauntValue || ""}
                    onChange={(e) => {
                      const nextPackagePrice = e.target.value;
                      setDiscauntValue(nextPackagePrice);
                      handleChange(
                        "discountValue",
                        parseFloat(nextPackagePrice),
                      );
                      setPricePerUnit(
                        toLinkedUnitPrice(
                          nextPackagePrice,
                          packQuantity || minQuantity,
                        ),
                      );
                    }}
                  />
                </div>
              </Col>
            </>
          )}
          {!hidesQuantityFields && (
            <Col sm={3.5}>
              <div className="field-group">
                <label className="field-label">תיאור מבצע (אופציונלי)</label>
                <textarea
                  className="promo-description"
                  placeholder="תיאור המבצע..."
                  value={description || ""}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    handleChange("description", e.target.value);
                  }}
                />
              </div>
            </Col>
          )}
        </Row>

        <Row className="promo-limits-row">
          <Col sm={4}>
            <div className="field-group small">
              <label className="field-label">הקצאה</label>
              <input
                type="number"
                className="number-input"
                placeholder="0"
                value={totalAllocatedQty ?? ""}
                onChange={(e) => {
                  setTotalAllocatedQty(e.target.value);
                  handleChange("totalAllocatedQty", parseInt(e.target.value));
                }}
              />
            </div>
          </Col>
          <Col sm={4}>
            <div className="field-group small">
              <label className="field-label">הגבלת קניה לסל</label>
              <input
                type="number"
                className="number-input"
                placeholder="0"
                value={maxPerPurchase ?? ""}
                onChange={(e) => {
                  setMaxPerPurchase(e.target.value);
                  handleChange("maxPerPurchase", parseInt(e.target.value));
                }}
              />
            </div>
          </Col>
          <Col sm={4}>
            <div className="field-group small">
              <label className="field-label">הגבלת קניה לתקופת המבצע</label>
              <input
                type="number"
                className="number-input"
                placeholder="0"
                value={maxPerPeriod ?? ""}
                onChange={(e) => {
                  setMaxPerPeriod(e.target.value);
                  handleChange("maxPerPeriod", parseInt(e.target.value));
                }}
              />
            </div>
          </Col>
        </Row>
        <Row className="promo-save-row">
          <Col sm={4}>
            <div className="field-group">
              <label className="field-label">תאריך התחלה</label>
              <input
                type="date"
                className="date-input"
                value={promoStartDate.split("T")[0] || ""}
                onChange={(e) => {
                  setPromoStartDate(e.target.value);
                  handleChange(
                    "startDate",
                    formatDateTimeForJerusalem(e.target.value),
                  );
                }}
              />
            </div>
          </Col>
          <Col sm={4}>
            <div className="field-group">
              <label className="field-label">תאריך סיום</label>
              <input
                type="date"
                className="date-input"
                value={promoEndDate.split("T")[0] || ""}
                onChange={(e) => {
                  setPromoEndDate(e.target.value);
                  handleChange(
                    "endDate",
                    formatDateTimeForJerusalem(e.target.value, {
                      endOfDay: true,
                    }),
                  );
                }}
              />
            </div>
          </Col>
          <Col sm={4}>
            <div className="date-actions-column">
              <button
                type="button"
                className="date-quick-btn"
                onClick={setEndOfMonth}
              >
                עד סוף חודש
              </button>

              <button
                type="button"
                className="date-quick-btn"
                onClick={setEndOfYear}
              >
                עד סוף שנה
              </button>
            </div>
          </Col>
        </Row>

        {showSaveButton && (
          <div className="save-button-row">
            <button
              type="button"
              className="ui-btn-brand save-promo-btn"
              onClick={handleSavePromo}
              disabled={isSavingPromo}
            >
              {isSavingPromo ? "שומר..." : "שמור"}
            </button>
          </div>
        )}
      </div>
      {showFamilyDialog && familyCategory && (
        <FamilyChangeDialog
          product={product}
          onConfirm={handleSaveFamilyPromo}
          close={() => setShowFamilyDialog(false)}
          markedProducts={familyMarkedProducts}
          massage={"סמן את המוצרים שהינך רוצה שיהיו כלולים במבצע:"}
          slug={familyCategory.slug}
        />
      )}
    </>
  );
};

export default Promotion;
